import { FC, ReactNode, useRef } from 'react';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';

import { DraggableContent } from './draggable-content';
import { useElementSize } from './hooks/use-element-size';
import { usePersistedPosition } from './hooks/use-persisted-position';
import { useResponsivePosition } from './hooks/use-responsive-position';
import { getRelativeFromPosition } from './utils';

interface FloatingDraggableProps {
  children: ReactNode;
  className?: string;
  defaultPosition?: { right: number; bottom: number };
  storageKey?: string;
}

export const FloatingDraggable: FC<FloatingDraggableProps> = ({
  children,
  className = '',
  defaultPosition = { right: 16, bottom: 16 },
  storageKey = 'floating-draggable-position',
}) => {
  const elementRef = useRef<HTMLDivElement>(null);

  const elementSize = useElementSize(elementRef);
  const { relativePosition, setRelativePosition } = usePersistedPosition({
    storageKey,
    defaultPosition,
  });
  const { position, setPosition } = useResponsivePosition({
    relativePosition,
    elementSize,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { delta } = event;
    const newPosition = {
      x: position.x + delta.x,
      y: position.y + delta.y,
    };

    setPosition(newPosition);
    setRelativePosition(getRelativeFromPosition(newPosition, elementSize));
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd} modifiers={[restrictToWindowEdges]}>
      <DraggableContent ref={elementRef} className={className} position={position}>
        {children}
      </DraggableContent>
    </DndContext>
  );
};
